import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 70, left: 60 };
const CHART_HEIGHT = 360;
const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6'];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
    marginLeft: '16px',
  },
  select: {
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  toggleGroup: {
    display: 'flex',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  toggleButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s',
  },
  toggleActive: {
    background: '#2563eb',
    color: '#fff',
  },
  toggleInactive: {
    background: '#fff',
    color: '#6b7280',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function SpectralDensityChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedField, setSelectedField] = useState('all');
  const [logScale, setLogScale] = useState(false);

  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Determine which fields to render
    const fieldsToRender = selectedField === 'all'
      ? data.fields
      : data.fields.filter((f) => f.field === selectedField);

    if (fieldsToRender.length === 0) return;

    // Compute global domains
    let freqMin = Infinity;
    let freqMax = -Infinity;
    let powerMin = Infinity;
    let powerMax = -Infinity;
    fieldsToRender.forEach((f) => {
      f.frequencies.forEach((freq) => {
        if (freq < freqMin) freqMin = freq;
        if (freq > freqMax) freqMax = freq;
      });
      f.power.forEach((p) => {
        if (p < powerMin) powerMin = p;
        if (p > powerMax) powerMax = p;
      });
    });

    const freqPad = (freqMax - freqMin) * 0.05 || 0.01;
    const x = d3.scaleLinear()
      .domain([Math.max(0, freqMin - freqPad), freqMax + freqPad])
      .range([0, innerW]);

    let y;
    if (logScale) {
      const safeMin = Math.max(0.01, powerMin);
      y = d3.scaleLog()
        .domain([safeMin, powerMax * 1.2])
        .nice()
        .range([innerH, 0]);
    } else {
      y = d3.scaleLinear()
        .domain([0, powerMax * 1.15])
        .nice()
        .range([innerH, 0]);
    }

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis (frequency)
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).ticks(8).tickFormat((v) => v.toFixed(2)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 34)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Normalized Frequency');

    // Secondary x-axis: period labels
    // Show period tick marks below frequency axis
    const periodTicks = [];
    fieldsToRender[0].frequencies.forEach((freq, i) => {
      const period = fieldsToRender[0].periods[i];
      if (period != null) {
        periodTicks.push({ freq, period });
      }
    });

    // Only show a subset to avoid clutter
    const periodSubset = periodTicks.filter((_, i) => i % 2 === 0 || i === periodTicks.length - 1);

    g.selectAll('.period-tick')
      .data(periodSubset)
      .enter()
      .append('text')
      .attr('x', (d) => x(d.freq))
      .attr('y', innerH + 50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text((d) => `${d.period.toFixed(1)}`);

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 62)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('Period (scrape intervals)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => {
            if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
            if (v >= 1) return v.toFixed(1);
            return v.toFixed(2);
          })
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -44)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text(logScale ? 'Spectral Power (log)' : 'Spectral Power');

    // Draw area + line for each field
    fieldsToRender.forEach((fieldData, fieldIdx) => {
      const colorIdx = data.fields.indexOf(fieldData);
      const color = COLORS[colorIdx % COLORS.length];

      // Build data points
      const points = fieldData.frequencies.map((freq, i) => ({
        freq,
        power: fieldData.power[i],
        period: fieldData.periods[i],
      }));

      // Clamp power for log scale
      const safePoints = logScale
        ? points.map((p) => ({ ...p, power: Math.max(0.01, p.power) }))
        : points;

      // Area
      const area = d3.area()
        .x((d) => x(d.freq))
        .y0(innerH)
        .y1((d) => y(d.power))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(safePoints)
        .attr('d', area)
        .attr('fill', color)
        .attr('fill-opacity', selectedField === 'all' ? 0.08 : 0.15)
        .attr('stroke', 'none');

      // Line
      const line = d3.line()
        .x((d) => x(d.freq))
        .y((d) => y(d.power))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(safePoints)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', selectedField === 'all' ? 1.5 : 2)
        .attr('stroke-opacity', 0.9);

      // Dominant frequency marker
      if (fieldData.dominantFrequency != null && fieldData.dominantPower != null) {
        const domFreq = fieldData.dominantFrequency;
        const domPower = logScale ? Math.max(0.01, fieldData.dominantPower) : fieldData.dominantPower;
        const domPeriod = fieldData.dominantPeriod;

        // Vertical dashed line at dominant frequency
        g.append('line')
          .attr('x1', x(domFreq))
          .attr('x2', x(domFreq))
          .attr('y1', 0)
          .attr('y2', innerH)
          .attr('stroke', color)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3')
          .attr('stroke-opacity', 0.5);

        // Peak dot
        g.append('circle')
          .attr('cx', x(domFreq))
          .attr('cy', y(domPower))
          .attr('r', 4)
          .attr('fill', color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);

        // Annotation label
        const labelX = x(domFreq) + 8;
        const labelY = y(domPower) - 10;

        g.append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('font-size', '9px')
          .attr('font-weight', 600)
          .attr('fill', color)
          .text(`Peak: f=${domFreq}, T=${domPeriod != null ? domPeriod.toFixed(1) : '?'}`);
      }

      // Invisible hover dots for each data point
      safePoints.forEach((d, i) => {
        g.append('circle')
          .attr('cx', x(d.freq))
          .attr('cy', y(d.power))
          .attr('r', 8)
          .attr('fill', 'transparent')
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              field: fieldData.field,
              frequency: d.freq,
              period: d.period,
              power: fieldData.power[i],
              color,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, selectedField, logScale]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <div style={styles.title}>Spectral Density Analysis</div>
            <div style={styles.subtitle}>Identifying dominant cycles in pricing data</div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No spectral density data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>Spectral Density Analysis</div>
          <div style={styles.subtitle}>Identifying dominant cycles in pricing data</div>
        </div>
        <div style={styles.controls}>
          <select
            style={styles.select}
            value={selectedField}
            onChange={(e) => setSelectedField(e.target.value)}
          >
            <option value="all">All Fields</option>
            {data.fields.map((f) => (
              <option key={f.field} value={f.field}>{f.field}</option>
            ))}
          </select>
          <div style={styles.toggleGroup}>
            <button
              style={{
                ...styles.toggleButton,
                ...(!logScale ? styles.toggleActive : styles.toggleInactive),
              }}
              onClick={() => setLogScale(false)}
            >
              Linear
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(logScale ? styles.toggleActive : styles.toggleInactive),
              }}
              onClick={() => setLogScale(true)}
            >
              Log
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {data.fields.map((f, i) => {
          const color = COLORS[i % COLORS.length];
          const isVisible = selectedField === 'all' || selectedField === f.field;
          return (
            <div
              key={f.field}
              style={{
                ...styles.legendItem,
                opacity: isVisible ? 1 : 0.4,
              }}
              onClick={() => setSelectedField(selectedField === f.field ? 'all' : f.field)}
            >
              <div style={{
                width: 12,
                height: 3,
                background: color,
                borderRadius: 1,
              }} />
              <span>{f.field}</span>
              {f.dominantPeriod != null && isVisible && (
                <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                  (T={f.dominantPeriod.toFixed(1)})
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: tooltip.color, marginBottom: '4px' }}>
            {tooltip.field}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Frequency</span>
            <span style={{ fontWeight: 600 }}>{tooltip.frequency.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Period</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.period != null ? `${tooltip.period.toFixed(1)} intervals` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Power</span>
            <span style={{ fontWeight: 600 }}>{tooltip.power.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
